import { FC, useMemo, useRef } from "react";
import JoditEditor from "jodit-react";
import { Jodit } from "jodit/esm/jodit";
import SimpleLoader from "./SimpleLoader";
import { hasInjectedCode, normalizeRichTextValue } from "../../../../utils/sanitizeRichText";
import { getSafeJoditConfig } from "./UI/getSafeJoditConfig";

type Props = {
  setValue: (value: string) => void;
  value: string;
  disabled?: boolean;
  className?: string;
  height?: number;
  placeholder?: string;
  isMiniEditor?: boolean;
};

const MyEditor: FC<Props> = ({
  setValue,
  value,
  disabled,
  className,
  height = 500,
  placeholder,
  isMiniEditor = false,
}) => {
  const editorRef = useRef<Jodit | null>(null);
  const sanitizedValue = normalizeRichTextValue(value ?? "");

  const config = useMemo(
    () =>
      getSafeJoditConfig({
        readonly: disabled || false,
        height,
        placeholder: placeholder || "Start typing here...",
        buttons: isMiniEditor
          ? ["bold", "italic", "underline", "ul", "ol"]
          : [
              "bold",
              "italic",
              "underline",
              "strikethrough",
              "|",
              "superscript",
              "subscript",
              "|",
              "ul",
              "ol",
              "outdent",
              "indent",
              "|",
              "font",
              "fontsize",
              "brush",
              "paragraph",
              "|",
              "align",
              "undo",
              "redo",
              "|",
              "cut",
              "copy",
              "paste",
              "hr",
              "eraser",
              "symbol",
              "|",
              "link",
              "table",
              "|",
              "find",
              "replace",
              "|",
              "selectall",
              "print",
              "preview",
              "fullsize",
              "spellcheck",
            ],
      }),
    [disabled, height, placeholder, isMiniEditor]
  );

  if (value === undefined || value === null) {
    return <SimpleLoader />;
  }

  return (
    <div className={className}>
      <JoditEditor
        ref={editorRef as any}
        config={config}
        onChange={(newContent) => {
          if (!hasInjectedCode(newContent)) {
            return;
          }

          setValue(normalizeRichTextValue(newContent));
        }}
        onBlur={(newContent) => {
          setValue(normalizeRichTextValue(newContent));
        }}
        value={sanitizedValue}
      />
    </div>
  );
};

export { MyEditor };
